import type { NextPage } from 'next';
import {
  Filters,
  Language,
  PartnerProfileEnriched,
  ProfilePageType,
  SEOProps,
  SortingMethod,
  UserDetails,
} from '@common/types';
import { BiSessionContextProvider } from '@components/Contexts/BiSessionContext';
import { CurrencyContextProvider } from '@components/Contexts/CurrencyContext';
import { HeaderContextProvider } from '@components/Contexts/HeaderContext';
import { ProfileContextProvider } from '@components/Contexts/ProfileContext';
import { TemplatesContextProvider } from '@components/Contexts/TemplatesContext';
import NotFound from '@components/Profile/NotFound';
import NotValidEuEmptyState from '@components/Profile/NotValidEuEmptyState';
import Profile from '@components/Profile';
import ProfileHeader from '@components/ProfileHeader';
import { PROFILE_NOT_EU_VALID, PROFILE_NOT_FOUND } from '@utils/constants';
import { Service } from '@wix/ambassador-marketplace-categories-v1-category/types';
import { WixDesignSystemProvider } from '@wix/design-system';
import { GeoData } from '@wix/wix-common-aspects';
import { AccountCertification } from '@wix/ambassador-academy-certifications-v1-account-certification/types';

function ProfileWithContexts({
  error,
  userCurrency,
  currencyConversionRate,
  taxRate,
  directoryHref,
}: {
  error: string;
  userCurrency: string;
  currencyConversionRate: number;
  taxRate: number;
  directoryHref?: string | null;
}) {
  const profileInner =
    error === PROFILE_NOT_FOUND ? (
      <NotFound directoryHref={directoryHref} />
    ) : error === PROFILE_NOT_EU_VALID ? (
      <NotValidEuEmptyState />
    ) : (
      <Profile />
    );

  return (
    <CurrencyContextProvider
      currencyData={{
        currencyCode: userCurrency,
        conversionRate: currencyConversionRate,
      }}
      taxRate={taxRate}
    >
      <WixDesignSystemProvider features={{ newColorsBranding: true }}>
        {profileInner}
      </WixDesignSystemProvider>
    </CurrencyContextProvider>
  );
}

const PartnerProfilePage: NextPage<{
  partnerProfile: PartnerProfileEnriched;
  services: Array<Service>;
  categoriesForHeader: Array<Service> | undefined;
  staticLocaleUrl: string;
  geo: GeoData;
  language: string;
  languages: Array<Language>;
  origin: string;
  entry: any;
  isMyProfile: boolean;
  enabled: boolean;
  isRenderedInModal: boolean;
  isMobileUserAgent: boolean;
  error: string;
  host: string;
  seo: SEOProps;
  userCurrency: string;
  currencyConversionRate: number;
  shouldDisplayTraceabilityData: boolean;
  taxRate: number;
  numberOfTemplates: number;
  templatesNumberOfPages: number;
  filters: Filters;
  currentPage: number;
  sort: SortingMethod;
  templatesAverageScore: number;
  profilePageType: ProfilePageType;
  userDetails: UserDetails;
  accountCertifications: AccountCertification[];
  directoryHref?: string | null;
}> = ({
  partnerProfile,
  services,
  categoriesForHeader,
  staticLocaleUrl,
  geo,
  language,
  languages,
  origin,
  entry,
  isMyProfile,
  enabled,
  isRenderedInModal,
  isMobileUserAgent,
  host,
  error,
  seo: { title, metaTags, links, structureData },
  userCurrency,
  currencyConversionRate,
  shouldDisplayTraceabilityData,
  taxRate,
  numberOfTemplates,
  templatesNumberOfPages,
  currentPage,
  sort,
  templatesAverageScore,
  profilePageType,
  userDetails,
  accountCertifications,
  directoryHref,
}) => {
  const isProd = process.env.NODE_ENV === 'production';

  return (
    <>
      <ProfileHeader
        title={title}
        links={links}
        metaTags={metaTags}
        structureData={structureData}
        isProd={isProd}
      />
      <BiSessionContextProvider>
        <HeaderContextProvider
          services={categoriesForHeader || services}
          host={host}
          origin={origin}
          entry={entry}
        >
          <ProfileContextProvider
            partnerProfile={partnerProfile}
            services={services}
            staticLocaleUrl={staticLocaleUrl}
            geo={geo}
            language={language}
            languages={languages}
            origin={origin}
            host={host}
            entry={entry}
            isMyProfile={isMyProfile}
            enabled={enabled}
            isRenderedInModal={isRenderedInModal}
            isMobileUserAgent={isMobileUserAgent}
            userCurrency={userCurrency}
            currencyConversionRate={currencyConversionRate}
            shouldDisplayTraceabilityData={shouldDisplayTraceabilityData}
            profilePageType={profilePageType}
            userDetails={userDetails}
            accountCertifications={accountCertifications}
          >
            <TemplatesContextProvider
              numberOfResults={numberOfTemplates}
              numberOfPages={templatesNumberOfPages}
              isMobileUserAgent={isMobileUserAgent}
              origin={origin}
              entry={entry}
              host={host}
              currentPage={currentPage}
              sort={sort}
              templatesAverageScore={templatesAverageScore ?? 0}
            >
              <ProfileWithContexts
                error={error}
                userCurrency={userCurrency}
                currencyConversionRate={currencyConversionRate}
                taxRate={taxRate}
                directoryHref={directoryHref}
              />
            </TemplatesContextProvider>
          </ProfileContextProvider>
        </HeaderContextProvider>
      </BiSessionContextProvider>
    </>
  );
};

export default PartnerProfilePage;
